import { ReactQueryWrapperBase } from "$root/components/ReactQueryWrapper";
import type { Instrument } from "$root/functional-areas/instruments-editor/const";
import {
	type CompositionInRetrievingDataResponse,
	createInstrumentList,
	instrumentsRepository,
} from "$root/functional-areas/instruments/repository";
import { persistedColStateKeys } from "$root/functional-areas/user/userStorage";
import { AgGridWithModules } from "$root/third-party-integrations/ag-grid/ag-grid";
import { columnsDefinition } from "$root/ui-lib/table/column";
import { agExtra } from "$root/ui-lib/table/default-configuration";
import { useMappedQueries } from "$root/third-party-integrations/react-query/hooks";
import { Card } from "$root/widgets-architecture/layout/Card";
import { Banner } from "@mdotm/shadcn/legacy/components";
import { identity } from "@mdotm/utils/functions";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

export function ReviewUniverseCompositionCard(props: { uuid: string }): React.JSX.Element {
	const queryReviewComposition = instrumentsRepository.reviewComposition.useQuery(props.uuid, "UNIVERSE");
	const userInstrumentsMapQuery = useQuery({
		...instrumentsRepository.userClassifications.getQueryConfig(
			queryReviewComposition.data?.composition?.map((entry) => entry.ticker).filter((ticker) => ticker != null) ?? [],
		),
		enabled: queryReviewComposition.data != null,
	});
	const queryInvestmentsRichInfo = useQuery({
		...instrumentsRepository.investmentsRichInfo.getQueryConfig(queryReviewComposition.data?.composition ?? []),
		enabled: queryReviewComposition.data != null,
	});

	const query = useMappedQueries(
		identity,
		[],
		[queryReviewComposition, userInstrumentsMapQuery, queryInvestmentsRichInfo],
	);

	return (
		<ReactQueryWrapperBase query={query}>
			{([compositionWithCash, userInstrumentsMap, investmentsRichInfo]) => (
				<ReviewUniverseCompositionCardInner
					compositionWithCash={compositionWithCash}
					instruments={createInstrumentList({
						composition: compositionWithCash.composition ?? [],
						userInstrumentsMap,
						investmentData: investmentsRichInfo,
					})}
				/>
			)}
		</ReactQueryWrapperBase>
	);
}

function ReviewUniverseCompositionCardInner({ instruments }: CompositionInRetrievingDataResponse) {
	const { t } = useTranslation();
	const columns = columnsDefinition.useInstrumentUniverseComposition("read");
	return (
		<Card>
			<div className="mb-4">
				<Banner severity="info">{t("PORTFOLIO_UPLOAD_ALERTS.CALCULATING_UPLOAD")}</Banner>
			</div>
			<AgGridWithModules<Instrument>
				{...agExtra.usePersistColState(persistedColStateKeys["tableV3-universe-composition-read"])}
				columnDefs={columns}
				rowData={instruments}
				getRowId={(x) => x.data.tickerName!}
			/>
		</Card>
	);
}
